<template>
  <div class="text-dark mx-auto" style="width: 736px">
    <div
      v-if="localModel.form_design === 'external'"
      class="flex mb-4 shadow border border-grey-300"
    >
      <div class="w-full">
        <div class="text-center bg-primary text-primary-invert pt-4 pb-4">
          <div class="flex">
            <div class="w-2/5">
              <span
                class="float-right mr-4 border-2 text-primary-invert border-primary-invert rounded-full h-16 w-16 flex items-center justify-center"
              >
                <i class="fa fa-check fa-2x"></i>
              </span>
            </div>
            <div
              v-if="localModel.form_design === 'groovepage'"
              class="w-3/5 text-left text-left font-medium mr-24"
            >
              <span class="text-3xl">
                {{
                  $t('frontend_groovemail_forms_step6_congratulations')
                }}...</span
              >
              <p class="text-lg font-semibold">
                {{ $t('frontend_groovemail_forms_step6_this_form') }}
              </p>
            </div>
            <div v-else class="w-3/5 text-left text-left font-medium mr-24">
              <span class="text-3xl">
                {{ $t('frontend_groovemail_forms_step6_congratulations') }}
                ...</span
              >
              <div class="mb-8">
                <p class="text-sm font-semibold text-grey-700">
                  {{ $t('frontend_groovemail_forms_step6_your_form') }}
                </p>
              </div>
              <div class="mb-4">
                <h4 class="text-xl font-semibold">
                  {{ $t('frontend_groovemail_forms_step6_what_next') }}
                </h4>
              </div>
              <div class="mb-2">
                <p class="text-sm font-semibold text-grey-700">
                  {{ $t('frontend_groovemail_forms_step6_paste') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="mt-16">
      <div class="flex flex-col items-center">
        <CheckmarkIcon class="h-13 w-13 text-green-extra-dark"/>
        <div class="my-4" style="font-size: 26px; line-height: 32px">
          {{ $t('frontend_groovemail_forms_step6_congratulations') }}
        </div>
        <div class="text-base">
          {{ $t('frontend_groovemail_forms_step6_your_form') }}
        </div>
      </div>
      <div class="text-xl font-semi-bold mt-5 mb-4">
        {{ $t('frontend_groovemail_forms_step6_embed_code') }}
      </div>
      <div class="text-xs text-grey-700">
        {{ $t('frontend_groovemail_forms_select_a_preset') }}
      </div>

      <gd-dropdown
        class="mt-2 mb-6 text-sm"
        v-model="activePresetId"
        :options="presetsSelect"
        size="full"
      />

      <div class="flex justify-between text-xs text-grey-700">
        <div>
          {{ $t('frontend_groovemail_forms_step6_copy_paste_code') }}
        </div>
        <div>
          <div
            class="cursor-pointer"
            @click="addcode('html')"
            v-if="showCodeLink"
          >
            {{ $t('frontend_groovemail_forms_step6_get_html_code') }}
          </div>
          <div class="cursor-pointer" @click="addcode('javascript')" v-else>
            {{ $t('frontend_groovemail_forms_step6_get_embed_code') }}
          </div>
        </div>
      </div>
      <div  class="mt-3 mb-13 relative">
        <Embed-Area
          class="py-2"
          variant="dark"
          v-model="htmlcode"
          style="height: 110px; font-size: 14px"
        />
        <div
          class="absolute cursor-pointer"
          v-clipboard:copy="htmlcode"
          v-clipboard:success="onCopyEmbedCode"
          v-clipboard:error="onErrorEmbedCode"
          style="right: 24px; top: 24px"
        >
          <CopyIcon class="text-primary h-6 w-6"/>
        </div>
      </div>
    </div>

    <div id="formhtml" style="display: none" v-if="activePreset && activePreset.styles">
      <html-raw-form class-prefix="groovemail-form-raw-html-embed-" :active-preset="activePreset" :mailform="localModel" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Notify from '@groovepages/gd-ui-kit/notify';
import EmbedArea from '@groovepages/gd-ui-kit/components/EmbedArea.vue';
import HtmlRawForm from '../../components/HtmlRawForm.vue';

// import Icons
import CheckmarkIcon from '@/assets/images/icons/new/checkmark_new.svg';
import CopyIcon from '@/assets/images/icons/new/copy_new.svg';

export default {
  name: 'Step6',
  props: {
    model: {
      required: true,
    },
  },

  data() {
    return {
      localModel: this.model,
      widgetUrl: '',
      embedUrl: '',
      ipaddress: '',
      apiUrl: '',
      htmlcode: '',
      parmalink: '',
      emailReg:
        '[_a-zA-Z0-9-]+(.[_a-zA-Z0-9-]+)*@[a-zA-Z0-9-]+(.[a-zA-Z0-9-]+)*(.[a-zA-Z]{2,4})',
      options: [
        {
          id: 'html',
          title: this.$t('frontend_groovemail_forms_step6_rawhtml'),
          description: this.$t('frontend_groovemail_forms_step6_the_html'),
        },
        {
          id: 'javascript',
          title: this.$t('frontend_groovemail_forms_step6_the_js'),
          description: this.$t('frontend_groovemail_forms_step6_easiest'),
        },
      ],
      showCodeLink: true,
    };
  },
  computed: {
    ...mapGetters({
      activePreset: 'forms/activePreset',
    }),
    ...mapState({
      presets: state => state.forms.presets,
    }),
    activePresetId: {
      get() { return this.$store.state.forms.activePresetId; },
      async set(value) {
        // show loader
        this.showLoader();

        await this.$store.dispatch('forms/setActivePreset', value);
        this.addcode(this.showCodeLink ? 'javascript' : 'html');

        // hide loader
        this.hideLoader();
      }
    },
    presetsSelect() {
      // get the list of presets that are not a local preset
      const presetsList = this.presets.filter(preset => preset.form_id === null || preset.form_id === undefined);

      // get the target form local preset if it exists
      const targetPreset = this.presets.find(preset => preset.form_id === this.localModel.formid);

      if(targetPreset) {
        // Add a new value to the top of the array
        presetsList.unshift(targetPreset);
      }

      return presetsList.map((variant) => {
        return {value: variant.id, text: variant.name };
      });
    },
  },
  components: {
    CheckmarkIcon,
    HtmlRawForm,
    CopyIcon,
    EmbedArea,
  },
  watch: {
    localModel() {
      this.$emit('updateModel', this.localModel);
    },
  },

  async created() {
    // show loader
    this.showLoader();

    this.apiUrl = window.config.apiUrl;
    // embed url = input: url/groovemail/
    this.embedUrl = window.config.grooveappsurl + this.$store.getters.supBrand.url;
    this.parmalink = window.btoa(this.localModel.formid);
    this.telephoneInputId = `telephone_input${this.localModel.formid}`;

    // hide loader
    this.hideLoader();
  },

  async mounted() {
    const formhtml = document.querySelector('#formhtml');
    this.htmlcode = formhtml.innerHTML;
    this.addcode('javascript');
  },

  methods: {
    // show loader
    showLoader() {
      this.$emit('showLoader');
    },

    // hide loader
    hideLoader() {
      this.$emit('hideLoader');
    },

    validate() {
      const isValid = true;
      this.$emit('on-validate', isValid);
      return isValid;
    },

    addcode(codeType) {
      if (codeType === 'html') {
        this.showCodeLink = false;
        const formhtml = document.querySelector('#formhtml');
        this.htmlcode = `${formhtml.firstChild.innerHTML}<link rel="stylesheet" type="text/css" href="${this.embedUrl}embed/app.css"/><script src="${this.embedUrl}embed/countryCodeInput.js" formId="${this.localModel.formid}"><\/script>`;
      } else {
        this.showCodeLink = true;
        this.htmlcode = '';
        const variation = this.activePresetId !== undefined ? `variation="${this.activePresetId}"` : '';
        const iframecode = `<groovemail-form-embed permalink="${this.parmalink}" ${variation}></groovemail-form-embed>`;
        this.htmlcode = `${iframecode}<script src="${this.embedUrl}embed/app.js"><\/script>`;
      }
    },

    onCopyEmbedCode() {
      Notify.success(
        this.$t('frontend_groovemail_forms_embed_code_copy_success')
      );
    },

    onErrorEmbedCode() {
      Notify.fail(this.$t('frontend_groovemail_forms_embed_code_copy_error'));
    },
  },
};
</script>
